// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { defineMessages, FormattedNumber } from 'react-intl'
import classNames from 'classnames'

import Toggle from '@ttn-lw/components/panel/toggle'
import Icon, { IconApplication, IconDevice, IconGateway, IconPlus } from '@ttn-lw/components/icon'
import Checkbox from '@ttn-lw/components/checkbox'
import ModalButton from '@ttn-lw/components/button/modal-button'
import toast from '@ttn-lw/components/toast'
import VerticalScrollFader from '@ttn-lw/components/vertical-scroll-fader'

import Pill from '@console/components/pill/index.tti'
import { generateRandomHexColor } from '@console/components/pill/utils.tti'

import attachPromise from '@ttn-lw/lib/store/actions/attach-promise'
import PropTypes from '@ttn-lw/lib/prop-types'
import sharedMessages from '@ttn-lw/lib/shared-messages'

import { checkFromState, mayCreateLabels } from '@console/lib/feature-checks'

import { updateApplication } from '@console/store/actions/applications'
import { createLabel, getLabelList } from '@console/store/actions/labels.tti'
import { updateDevice } from '@console/store/actions/devices'
import { updateGateway } from '@console/store/actions/gateways'

import { selectLabels } from '@console/store/selectors/labels.tti'

import ModalForm from '../labels-table/modal-form.tti'

import style from './assign-labels.tti.styl'

const m = defineMessages({
  assignlabel: 'Assign label to {entityType}',
  existing: 'Existing',
  new: 'New',
})

const toggleOptions = [{ label: m.existing, value: 'existing' }]
const extendedToggleOptions = [...toggleOptions, { label: m.new, value: 'new' }]

const AssignLabels = ({ entityId, entityType, appId, edit, create, labelIds, buttonText }) => {
  const dispatch = useDispatch()
  const mayAdd = useSelector(state => checkFromState(mayCreateLabels, state))
  const [error, setError] = useState(undefined)
  const [active, setActive] = useState('existing')
  const [randomColor, setRandomColor] = useState(generateRandomHexColor())
  const [editedLabelName, setEditedLabelName] = useState('')
  const [editedLabelDescription, setEditedLabelDescription] = useState('')
  const [editedLabelColor, setEditedLabelColor] = useState(randomColor)
  const labelList = useSelector(selectLabels)
  const labelsValues = useMemo(
    () =>
      labelList.reduce((acc, label) => {
        acc[label.ids.label_id] = labelIds?.includes(label.ids.label_id)

        return acc
      }, {}),
    [labelList, labelIds],
  )
  const [labelFilterValues, setLabelFilterValues] = useState(labelsValues)
  const labelToFilter = Object.keys(labelFilterValues).filter(key => labelFilterValues[key])

  const onLabelFilterChange = useCallback(async value => {
    setLabelFilterValues(value)
  }, [])

  const onToggleChange = useCallback((_, value) => {
    setActive(value)
  }, [])

  const handleAssignLabel = useCallback(
    async labelIds => {
      let isMounted = true
      try {
        switch (entityType) {
          case 'application':
            await dispatch(
              attachPromise(updateApplication(entityId, { label_ids: labelIds ?? labelToFilter })),
            )
            break
          case 'device':
            await dispatch(
              attachPromise(
                updateDevice(appId, entityId, { label_ids: labelIds ?? labelToFilter }),
              ),
            )
            break
          case 'gateway':
            await dispatch(
              attachPromise(updateGateway(entityId, { label_ids: labelIds ?? labelToFilter })),
            )
            break
          default:
            break
        }
        toast({
          title: sharedMessages.success,
          message: sharedMessages.labelAssignSuccess,
          type: toast.types.SUCCESS,
        })
      } catch (error) {
        if (isMounted) {
          toast({
            title: sharedMessages.error,
            message: sharedMessages.labelAssignError,
            type: toast.types.ERROR,
          })
        }
      }

      return () => {
        isMounted = false
      }
    },
    [dispatch, entityId, entityType, appId, labelToFilter],
  )

  const handleCreateLabel = useCallback(async () => {
    let isMounted = true
    try {
      await dispatch(
        attachPromise(
          createLabel({
            ids: {
              label_id: editedLabelName.toLowerCase().split(' ').join('-'),
            },
            name: editedLabelName,
            description: editedLabelDescription,
            color: editedLabelColor,
          }),
        ),
      )
      handleAssignLabel([...labelIds, editedLabelName.toLowerCase().split(' ').join('-')])
      await dispatch(attachPromise(getLabelList()))
      setLabelFilterValues({
        ...labelFilterValues,
        [editedLabelName.toLowerCase().split(' ').join('-')]: true,
      })
      toast({
        title: sharedMessages.success,
        message: sharedMessages.createLabelSuccess,
        type: toast.types.SUCCESS,
      })
      setError(undefined)
    } catch (error) {
      setError(error)
      if (isMounted) {
        toast({
          title: sharedMessages.error,
          message: sharedMessages.createLabelError,
          type: toast.types.ERROR,
        })
      }
      return error
    }

    return () => {
      isMounted = false
    }
  }, [
    dispatch,
    editedLabelName,
    editedLabelDescription,
    editedLabelColor,
    handleAssignLabel,
    labelIds,
    labelFilterValues,
  ])

  const handleInputChange = React.useCallback(
    (name, value) => {
      switch (name) {
        case 'name':
          setEditedLabelName(value)
          break
        case 'description':
          setEditedLabelDescription(value)
          break
        case 'color':
          setEditedLabelColor(value)
          setRandomColor(value)
          break
        default:
          break
      }
    },
    [setEditedLabelName, setEditedLabelDescription, setEditedLabelColor],
  )

  const labelsListElement = useMemo(
    () =>
      labelList.map(label => (
        <React.Fragment key={label.ids.label_id}>
          <Checkbox className="p-cs-xs m-0 gap-cs-xs" name={label.ids.label_id} label="">
            <Pill color={label.color} text={label.name} />
            <div
              className={classNames(
                style.entities,
                'd-flex j-center gap-cs-m c-text-neutral-semilight',
              )}
            >
              <div className="d-flex al-center gap-cs-xxs">
                <Icon icon={IconApplication} />
                <FormattedNumber value={label.entities?.applications?.length || 0} />
              </div>
              <div className="d-flex al-center gap-cs-xxs">
                <Icon icon={IconGateway} />
                <FormattedNumber value={label.entities?.gateways?.length || 0} />
              </div>
              <div className="d-flex al-center gap-cs-xxs">
                <Icon icon={IconDevice} />
                <FormattedNumber value={label.entities?.end_devices?.length || 0} />
              </div>
            </div>
          </Checkbox>
          <hr className="w-full c-border-neutral-extralight" />
        </React.Fragment>
      )),
    [labelList],
  )

  const modalInitialValues = {
    ids: {
      label_id: '',
    },
    name: '',
    description: '',
    color: randomColor,
  }

  const modalChildren = (
    <div className="w-full">
      {!create && (
        <div className={style.toggleContainer}>
          <Toggle
            className="mb-cs-xl"
            active={active}
            options={mayAdd ? extendedToggleOptions : toggleOptions}
            onToggleChange={onToggleChange}
            fullWidth
          />
        </div>
      )}
      {active === 'existing' && !create && (
        <VerticalScrollFader className={style.checkboxList} faderHeight="3rem" light>
          <Checkbox.Group name="label_ids" value={labelFilterValues} onChange={onLabelFilterChange}>
            {labelsListElement}
          </Checkbox.Group>
        </VerticalScrollFader>
      )}
      {((active === 'new' && mayAdd) || (create && mayAdd)) && (
        <ModalForm
          initialValues={modalInitialValues}
          handleInputChange={handleInputChange}
          className="direction-column"
          randomColor={randomColor}
          setRandomColor={setRandomColor}
          error={error}
        />
      )}
    </div>
  )

  const handleCancel = useCallback(() => {
    setLabelFilterValues(labelsValues)
    setError(undefined)
  }, [setLabelFilterValues, labelsValues])

  return (
    <ModalButton
      type="button"
      icon={create ? undefined : IconPlus}
      smallIcon
      message={create ? buttonText[0] : edit ? '' : sharedMessages.addLabel}
      onApprove={active === 'new' || create ? handleCreateLabel : handleAssignLabel}
      onCancel={handleCancel}
      modalData={{
        children: modalChildren,
        title: create ? sharedMessages.addLabel : m.assignlabel,
        titleValues: { entityType },
        approveButtonProps: {
          primary: true,
          message: active === 'new' ? sharedMessages.createLabel : sharedMessages.apply,
          icon: null,
          disabled: active === 'new' && !Boolean(editedLabelName) && !Boolean(editedLabelColor),
        },
        cancelButtonProps: {
          message: sharedMessages.cancel,
          icon: null,
        },
        danger: false,
      }}
      secondary={!create}
      naked
      small
      className={
        create
          ? classNames(style.createOneText, 'p-0 fw-normal td-underline')
          : classNames('c-bg-neutral-semilight br-xxl fs-xs m-0', {
              'p-vert-cs-xxs lh-1': !edit,
              'h-content': !edit,
              'h-auto': edit,
            })
      }
      modalClassName={style.assignModal}
    />
  )
}

AssignLabels.propTypes = {
  appId: PropTypes.string,
  buttonText: PropTypes.arrayOf(PropTypes.string),
  create: PropTypes.bool,
  edit: PropTypes.bool,
  entityId: PropTypes.string.isRequired,
  entityType: PropTypes.string.isRequired,
  labelIds: PropTypes.arrayOf(PropTypes.string),
}

AssignLabels.defaultProps = {
  edit: false,
  labelIds: [],
  appId: undefined,
  create: false,
  buttonText: undefined,
}

export default AssignLabels
